import { useState } from 'react';
import { Card, Button, Badge, Avatar, Input, Textarea } from '@lib/ui';
import { Modal, Drawer } from '@lib/ui';

// Icons
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const EditIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"
    />
  </svg>
);

const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456Z"
    />
  </svg>
);

interface Persona {
  id: number;
  name: string;
  avatar: string;
  description: string;
  tone: string[];
  style: string[];
  channels: string[];
  contentCount: number;
  exampleOutput: string;
  color: string;
}

const personas: Persona[] = [
  {
    id: 1,
    name: 'Thought Leader',
    avatar: 'TL',
    description:
      'Strategic, authoritative voice for establishing industry expertise and sharing forward-thinking perspectives.',
    tone: ['Authoritative', 'Confident', 'Visionary'],
    style: ['Long-form', 'Data-driven', 'Storytelling'],
    channels: ['LinkedIn', 'Blog', 'Newsletter'],
    contentCount: 23,
    exampleOutput:
      'The future of content creation lies not in producing more, but in transforming single insights into contextually-adapted expressions across multiple channels.',
    color: 'bg-primary-500',
  },
  {
    id: 2,
    name: 'Tech Educator',
    avatar: 'TE',
    description:
      'Clear, practical voice for explaining complex technical concepts to diverse audiences.',
    tone: ['Approachable', 'Clear', 'Helpful'],
    style: ['Step-by-step', 'Examples', 'Analogies'],
    channels: ['Twitter', 'YouTube', 'Documentation'],
    contentCount: 15,
    exampleOutput:
      'Think of personas like filters for a camera—same scene, different looks. Your core message stays the same, but the presentation adapts to each audience.',
    color: 'bg-secondary-500',
  },
  {
    id: 3,
    name: 'Brand Voice',
    avatar: 'BV',
    description:
      'Consistent, professional voice that maintains brand identity across all communications.',
    tone: ['Professional', 'Consistent', 'Engaging'],
    style: ['Concise', 'On-brand', 'Polished'],
    channels: ['Website', 'Marketing', 'Email'],
    contentCount: 9,
    exampleOutput:
      'At HalfNote, we believe your ideas deserve to reach every corner of your audience—without the cognitive tax of manual adaptation.',
    color: 'bg-success-500',
  },
  {
    id: 4,
    name: 'Community Builder',
    avatar: 'CB',
    description:
      'Warm, inclusive voice for fostering engagement and building authentic connections.',
    tone: ['Warm', 'Inclusive', 'Conversational'],
    style: ['Questions', 'Personal', 'Engaging'],
    channels: ['Discord', 'Community', 'Social'],
    contentCount: 0,
    exampleOutput:
      "What if you could spend more time forming ideas and less time reformatting them? That's the question that sparked our journey. What's yours?",
    color: 'bg-warning-500',
  },
];

interface PersonaCardProps {
  persona: Persona;
  onEdit: () => void;
  onGenerate: () => void;
}

function PersonaCard({ persona, onEdit, onGenerate }: PersonaCardProps) {
  return (
    <Card className="p-6 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Avatar size="lg" className={persona.color} name={persona.avatar} />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{persona.name}</h3>
            <Badge variant="soft" color="primary" className="mt-1">
              {persona.contentCount} pieces generated
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="flat" isIcon className="size-8" onClick={onEdit}>
            <EditIcon />
          </Button>
        </div>
      </div>

      <p className="mt-4 text-sm text-gray-600 dark:text-dark-300">{persona.description}</p>

      {/* Tone & Style */}
      <div className="mt-4 space-y-3">
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
            Tone
          </p>
          <div className="flex flex-wrap gap-1">
            {persona.tone.map((t) => (
              <Badge key={t} variant="outlined" className="text-xs">
                {t}
              </Badge>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
            Style
          </p>
          <div className="flex flex-wrap gap-1">
            {persona.style.map((s) => (
              <Badge key={s} variant="outlined" className="text-xs">
                {s}
              </Badge>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
            Channels
          </p>
          <div className="flex flex-wrap gap-1">
            {persona.channels.map((c) => (
              <Badge key={c} variant="soft" color="info" className="text-xs">
                {c}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Example Output */}
      <div className="mt-4 p-3 bg-gray-50 dark:bg-dark-700 rounded-lg">
        <p className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
          Example Output
        </p>
        <p className="text-sm text-gray-700 dark:text-dark-200 italic">"{persona.exampleOutput}"</p>
      </div>

      {/* Actions */}
      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-600">
        <Button className="w-full" onClick={onGenerate}>
          <SparklesIcon />
          <span className="ml-2">Generate Content</span>
        </Button>
      </div>
    </Card>
  );
}

export default function Personas() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<Persona | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleEdit = (persona: Persona) => {
    setSelectedPersona(persona);
    setDrawerOpen(true);
  };

  const handleGenerate = (persona: Persona) => {
    setSelectedPersona(persona);
    // Navigate to content generation or open modal
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Personas</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Persona is a first-class primitive. Create reusable intellectual lenses for your
            content.
          </p>
        </div>
        <Button onClick={() => setCreateModalOpen(true)}>
          <PlusIcon />
          <span className="ml-2">Create Persona</span>
        </Button>
      </div>

      {/* Vision Quote */}
      <Card className="p-4 bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800">
        <p className="text-sm text-primary-800 dark:text-primary-200">
          "HalfNote separates <strong>what is being said</strong> from{' '}
          <strong>how it is said</strong>. By treating personas as reusable intellectual lenses,
          creators can compound their thinking and maintain a coherent presence across surfaces."
        </p>
      </Card>

      {/* Personas Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {personas.map((persona) => (
          <PersonaCard
            key={persona.id}
            persona={persona}
            onEdit={() => handleEdit(persona)}
            onGenerate={() => handleGenerate(persona)}
          />
        ))}
      </div>

      {/* Create Persona Modal */}
      <Modal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        title="Create New Persona"
        size="lg"
      >
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label="Persona Name" placeholder="e.g., Technical Expert" />
            <Input label="Avatar Initials" placeholder="e.g., TE" maxLength={2} />
          </div>

          <Textarea
            label="Description"
            placeholder="Describe the persona's purpose and when to use it..."
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
              Tone Characteristics
            </label>
            <Input placeholder="Enter tones separated by commas (e.g., Professional, Friendly, Expert)" />
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">
              Describe how this persona "sounds" to readers
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
              Writing Style
            </label>
            <Input placeholder="Enter styles separated by commas (e.g., Concise, Data-driven, Storytelling)" />
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">
              Describe the structural and formatting preferences
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
              Target Channels
            </label>
            <Input placeholder="Enter channels separated by commas (e.g., LinkedIn, Twitter, Blog)" />
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-400">
              Where will this persona's content typically be published?
            </p>
          </div>

          <Textarea
            label="Example Output"
            placeholder="Provide an example of how this persona would express an idea..."
            description="This helps calibrate the persona's voice"
          />

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-dark-600">
            <Button variant="flat" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button>Create Persona</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Persona Drawer */}
      <Drawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedPersona ? `Edit: ${selectedPersona.name}` : 'Edit Persona'}
        position="right"
        size="lg"
      >
        {selectedPersona && (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg">
              <Avatar size="lg" className={selectedPersona.color} name={selectedPersona.avatar} />
              <div>
                <Input defaultValue={selectedPersona.name} className="font-semibold" />
              </div>
            </div>

            <Textarea label="Description" defaultValue={selectedPersona.description} />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
                Tone Characteristics
              </label>
              <Input defaultValue={selectedPersona.tone.join(', ')} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
                Writing Style
              </label>
              <Input defaultValue={selectedPersona.style.join(', ')} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
                Target Channels
              </label>
              <Input defaultValue={selectedPersona.channels.join(', ')} />
            </div>

            <Textarea label="Example Output" defaultValue={selectedPersona.exampleOutput} />

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-dark-600">
              <Button variant="flat" onClick={() => setDrawerOpen(false)}>
                Cancel
              </Button>
              <Button>Save Changes</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
